import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  TablePagination,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonOff as PersonOffIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useGetCandidateScheduledList } from '../../hooks/useCandidate';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX } from '../common/tableStyles';
import { formatDate } from '../../utils/dateFormat';

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

const RejectedCandidate = () => {
  const { data: sheduledlist = [] } = useGetCandidateScheduledList();

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);



  const filteredCandidates = Array.isArray(sheduledlist)
    ? sheduledlist
      // Rejected at interview OR selected-then-dropped / did not join.
      .filter(
        (applicant) =>
          applicant.statuses?.some((status) => status.status === 'rejected') ||
          applicant.final_status === 'rejected'
      )
      .filter((applicant) =>
        applicant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        applicant.email_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        applicant.phone_number?.includes(searchTerm) ||
        applicant.role?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const paginatedCandidates = filteredCandidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <PersonOffIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                Rejected Candidates
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Applicants rejected at interview or dropped after selection
              </Typography>
            </Box>
          </Box>
          <Chip
            label={`${filteredCandidates.length} Candidates`}
            sx={{ fontWeight: 700, bgcolor: '#eef4ff', color: ACCENT, border: 'none', borderRadius: '10px' }}
          />
        </Stack>
      </Paper>

      {/* Filter / search card */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2 }, mb: { xs: 2, md: 3 } }}>
        <TextField
          size="small"
          placeholder="Search by Name, Email, Phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 320 }, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } }}
        />
      </Paper>

      {/* Table card */}
      <Paper elevation={0} sx={CARD_SX}>
        <TableContainer component={Paper} elevation={0} sx={{ ...TABLE_PAPER_SX, boxShadow: 'none', border: 'none' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_CELL_SX}>S.No</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Name</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Role</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Email</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Contact No</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Interview Date</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Reason</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Resume</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCandidates.length > 0 ? paginatedCandidates.map((applicant, index) => {
                const rejectionStatus = applicant.statuses?.find((s) => s.status === 'rejected');
                return (
                  <TableRow key={index} sx={TABLE_ROW_SX}>
                    <TableCell sx={BODY_CELL_SX}>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, fontWeight: 600 }}>{applicant.name || 'N/A'}</TableCell>
                    <TableCell sx={BODY_CELL_SX}>{applicant.role || 'N/A'}</TableCell>
                    <TableCell sx={BODY_CELL_SX}>{applicant.email_address || 'N/A'}</TableCell>
                    <TableCell sx={BODY_CELL_SX}>{applicant.phone_number || 'N/A'}</TableCell>
                    <TableCell sx={BODY_CELL_SX}>{applicant.interview_date ? formatDate(applicant.interview_date) : 'N/A'}</TableCell>
                    <TableCell sx={BODY_CELL_SX}>
                      <Chip
                        label={rejectionStatus?.rejection_reason || (applicant.final_status === 'rejected' ? 'Dropped / Did not join' : 'N/A')}
                        size="small"
                        sx={{ fontWeight: 700, bgcolor: '#fef2f2', color: '#ef4444', border: 'none', borderRadius: '8px', maxWidth: 220, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                      />
                    </TableCell>
                    <TableCell sx={BODY_CELL_SX}>
                      {applicant.resume ? (
                        <Link
                          href={applicant.resume}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: ACCENT, fontWeight: 700, textDecoration: 'none' }}
                        >
                          <DescriptionIcon fontSize="small" />
                          Resume
                        </Link>
                      ) : 'N/A'}
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8, border: 'none' }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PersonOffIcon />
                      </Box>
                      <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No Rejected Candidates Found</Typography>
                      <Typography variant="body2" sx={{ color: '#9ca3af' }}>Try adjusting your search or check back later.</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredCandidates.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default RejectedCandidate;
